import React, { useCallback, useRef } from 'react';
import { WINDOW_SEC, WINDOW_DAYS } from '../data/swellArchive';

// Shared Live/History window selector for the swell surfaces (map, globe,
// forecast). Emits { mode:'live' } or { mode:'history', startSec, endSec } where
// the window is a fixed ~86-day span ending on the chosen date. Renders a small
// overview strip of the available archive extent with a draggable window band.

const DAY = 86400;
const toISODate = (sec) => new Date(sec * 1000).toISOString().slice(0, 10);
const fromISODate = (str) => Math.floor(Date.parse(`${str}T12:00:00Z`) / 1000);
const fmt = (sec) => new Date(sec * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export default function SwellWindow({ extent, value, onChange, busy, note }) {
  const trackRef = useRef(null);
  const minTime = extent?.minTime;
  const maxTime = extent?.maxTime;
  const hasArchive = Number.isFinite(minTime) && Number.isFinite(maxTime) && maxTime > minTime + DAY;

  const mode = value?.mode === 'history' && hasArchive ? 'history' : 'live';
  const endSec = clamp(
    value?.endSec || maxTime || Math.floor(Date.now() / 1000),
    hasArchive ? minTime + Math.min(WINDOW_SEC, maxTime - minTime) : 0,
    hasArchive ? maxTime : Math.floor(Date.now() / 1000)
  );
  const startSec = Math.max(hasArchive ? minTime : endSec - WINDOW_SEC, endSec - WINDOW_SEC);

  const emitHistory = useCallback((end) => {
    const e = clamp(end, minTime + Math.min(WINDOW_SEC, maxTime - minTime), maxTime);
    onChange({ mode: 'history', endSec: e, startSec: Math.max(minTime, e - WINDOW_SEC) });
  }, [minTime, maxTime, onChange]);

  const seekFromPointer = useCallback((clientX) => {
    const el = trackRef.current;
    if (!el || !hasArchive) return;
    const rect = el.getBoundingClientRect();
    const frac = clamp((clientX - rect.left) / (rect.width || 1), 0, 1);
    emitHistory(minTime + frac * (maxTime - minTime));
  }, [emitHistory, hasArchive, minTime, maxTime]);

  const dragging = useRef(false);
  const onDown = (e) => { dragging.current = true; seekFromPointer(e.clientX); };
  const onMove = (e) => { if (dragging.current) seekFromPointer(e.clientX); };
  const endDrag = () => { dragging.current = false; };

  const span = hasArchive ? maxTime - minTime : 1;
  const bandLeft = hasArchive ? ((startSec - minTime) / span) * 100 : 0;
  const bandWidth = hasArchive ? ((endSec - startSec) / span) * 100 : 0;

  return (
    <div className="cs-swin">
      <div className="cs-swin-modes">
        <button className={mode === 'live' ? 'on' : ''} onClick={() => onChange({ mode: 'live' })}>Live</button>
        <button
          className={mode === 'history' ? 'on' : ''}
          disabled={!hasArchive}
          title={hasArchive ? 'Explore a past window' : 'Archive is still accumulating — history unlocks as data banks up'}
          onClick={() => emitHistory(value?.endSec || maxTime)}
        >
          History
        </button>
      </div>

      {mode === 'history' && hasArchive && (
        <>
          <label className="cs-swin-date">
            <span>Window ends</span>
            <input
              className="cs-input cs-input-sm"
              type="date"
              min={toISODate(minTime)}
              max={toISODate(maxTime)}
              value={toISODate(endSec)}
              onChange={(e) => { if (e.target.value) emitHistory(fromISODate(e.target.value)); }}
            />
          </label>
          <button className="cs-swin-step" title="Back one window" onClick={() => emitHistory(endSec - WINDOW_SEC)}>◀ {WINDOW_DAYS}d</button>
          <button className="cs-swin-step" title="Forward one window" onClick={() => emitHistory(endSec + WINDOW_SEC)} disabled={endSec >= maxTime}>{WINDOW_DAYS}d ▶</button>
          <div
            ref={trackRef}
            className="cs-swin-track"
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={endDrag}
            onPointerLeave={endDrag}
            title="Drag to choose a past window"
          >
            <span className="cs-swin-band" style={{ left: `${bandLeft}%`, width: `${Math.max(2, bandWidth)}%` }} />
          </div>
          <span className="cs-swin-range">
            {fmt(startSec)} → {fmt(endSec)}{busy ? ' · loading…' : ''}
          </span>
        </>
      )}
      {note && <span className="cs-swin-note">{note}</span>}
    </div>
  );
}
